import React, { useState } from "react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Mail, MapPin, Phone } from "lucide-react";
import { api } from "@/lib/api";
import { TID } from "@/lib/testIds";

export default function ContactPage() {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [submitting, setSubmitting] = useState(false);

  const onChange = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.subject.trim() || !form.message.trim()) {
      toast.error("Por favor completá todos los campos.");
      return;
    }
    setSubmitting(true);
    try {
      const res = await api.contact(form);
      toast.success(res.message || "Mensaje enviado. Gracias por escribirnos.");
      setForm({ name: "", email: "", subject: "", message: "" });
    } catch {
      toast.error("No se pudo enviar el mensaje. Intentá nuevamente.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid={TID.contactPage} className="container-wide py-10">
      <div className="text-xs uppercase tracking-[0.18em] text-turquoise font-semibold mb-2">Escribinos</div>
      <h1 className="font-serif font-black text-4xl sm:text-5xl text-blueblack">Contacto</h1>
      <p className="mt-3 text-textgray max-w-2xl leading-relaxed">
        Escribinos por consultas de prensa, denuncias, sugerencias o propuestas de contenido. Respondemos en un plazo máximo de 48 horas hábiles.
      </p>
      <div className="mt-6 h-[3px] w-16 bg-watergreen" />

      <div className="mt-12 grid grid-cols-1 lg:grid-cols-3 gap-10">
        <form onSubmit={submit} className="lg:col-span-2 bg-white border border-softblue p-6 sm:p-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <div>
              <Label htmlFor="name" className="text-xs uppercase tracking-widest text-textgray font-semibold">Nombre</Label>
              <Input
                id="name"
                data-testid={TID.contactName}
                value={form.name}
                onChange={onChange("name")}
                placeholder="Tu nombre"
                className="mt-2 h-11 bg-lightgray border-softblue focus-visible:ring-turquoise"
              />
            </div>
            <div>
              <Label htmlFor="email" className="text-xs uppercase tracking-widest text-textgray font-semibold">Email</Label>
              <Input
                id="email"
                type="email"
                data-testid={TID.contactEmail}
                value={form.email}
                onChange={onChange("email")}
                placeholder="tu@email.com"
                className="mt-2 h-11 bg-lightgray border-softblue focus-visible:ring-turquoise"
              />
            </div>
          </div>
          <div className="mt-5">
            <Label htmlFor="subject" className="text-xs uppercase tracking-widest text-textgray font-semibold">Asunto</Label>
            <Input
              id="subject"
              data-testid={TID.contactSubject}
              value={form.subject}
              onChange={onChange("subject")}
              placeholder="Motivo del contacto"
              className="mt-2 h-11 bg-lightgray border-softblue focus-visible:ring-turquoise"
            />
          </div>
          <div className="mt-5">
            <Label htmlFor="message" className="text-xs uppercase tracking-widest text-textgray font-semibold">Mensaje</Label>
            <Textarea
              id="message"
              data-testid={TID.contactMessage}
              value={form.message}
              onChange={onChange("message")}
              placeholder="Contanos con detalle…"
              rows={7}
              className="mt-2 bg-lightgray border-softblue focus-visible:ring-turquoise"
            />
          </div>
          <Button
            type="submit"
            data-testid={TID.contactSubmit}
            disabled={submitting}
            className="mt-6 bg-nightblue hover:bg-blueblack text-white rounded-none h-11 px-8 font-semibold uppercase text-xs tracking-widest"
          >
            {submitting ? "Enviando…" : "Enviar mensaje"}
          </Button>
        </form>

        <aside className="bg-nightblue text-white p-6 sm:p-8">
          <h3 className="font-serif text-2xl font-bold">Redacción Sur Primicia</h3>
          <p className="mt-2 text-sm text-white/70 leading-relaxed">
            Nuestro equipo edita desde Río Grande. Estamos disponibles todos los días del año.
          </p>
          <ul className="mt-6 space-y-4 text-sm">
            <li className="flex items-start gap-3">
              <Mail className="w-4 h-4 mt-0.5 text-watergreen" />
              <span>redaccion@surprimicia.com.ar</span>
            </li>
            <li className="flex items-start gap-3">
              <Phone className="w-4 h-4 mt-0.5 text-watergreen" />
              <span>+54 2901 000-000</span>
            </li>
          </ul>
          <div className="mt-8 pt-6 border-t border-white/10 text-xs uppercase tracking-widest text-white/60">
            Zona horaria: America/Argentina/Ushuaia (UTC−3)
          </div>
        </aside>
      </div>
    </div>
  );
}
